import type { WelcomeTip } from '@/features/onboarding/lib/welcome-tips'
import { COMPED_PLAN_LABEL } from '@/features/settings/billing/subscription-status'
import type { TutorialId } from '@/features/tutorial/lib/tutorials'
import {
  BillingFeature,
  getMaxSharedMembers,
  hasFeature,
  isAlgoAllowed,
  isFreePlanLabel,
  planLevelOrder,
} from '@/lib/billing/capabilities'
import { AlgoType } from '@/types/generated/algo'
import { PlanLevel } from '@/types/generated/common/account'
import type { Capabilities, GetSubscriptionResponse, Plan } from '@/types/generated/public_api'

/**
 * The three welcome-tour scenarios. Basic tours as `pro` (a paid plan): the steps it can't
 * use are dropped by capability, not by tier, so it never points at something locked.
 */
export const TOUR_PLANS = ['essential', 'pro', 'custom'] as const
export type TourPlan = (typeof TOUR_PLANS)[number]

/**
 * Which scenario a subscription gets; null while it hasn't loaded (or failed), when the tour
 * says nothing about plans rather than guess one. CUSTOM (an operator comp) and Enterprise
 * both tour as `custom`: their capabilities can exceed any public plan.
 */
export function tourPlanOf(
  subscription: Pick<GetSubscriptionResponse, 'planLevel' | 'planLabel'> | undefined
): TourPlan | null {
  if (!subscription) return null
  const { planLevel, planLabel } = subscription
  if (planLabel === COMPED_PLAN_LABEL || planLevel === PlanLevel.PLAN_LEVEL_ENTERPRISE) {
    return 'custom'
  }
  if (
    isFreePlanLabel(planLabel) ||
    planLevel === PlanLevel.PLAN_LEVEL_FREE ||
    planLevel === PlanLevel.PLAN_LEVEL_UNSPECIFIED
  ) {
    return 'essential'
  }
  return 'pro'
}

/**
 * The steps each plan's tour adds after the workspace basics: what the plan includes (and
 * where each locked feature starts), the plan's own pages in the side panel, and where the
 * tutorials live. Without a known plan, just the last.
 */
const PLAN_TIPS: Record<TourPlan, readonly WelcomeTip[]> = {
  essential: ['plan', 'tutorials'],
  pro: ['plan', 'riskSlidesNav', 'hedgeNav', 'arbNav', 'tutorials'],
  custom: ['plan', 'riskSlidesNav', 'hedgeNav', 'arbNav', 'team', 'tutorials'],
}

export function planTipsFor(plan: TourPlan | null): readonly WelcomeTip[] {
  return plan ? PLAN_TIPS[plan] : ['tutorials']
}

/** A feature the plan step lists, with whether a plan's capabilities include it. */
export interface PlanFeature {
  /** Copy key under `trade.welcomeGuide.tour.plan.features`. */
  key: 'riskSlides' | 'advancedTwap' | 'rfq' | 'spreader' | 'arb' | 'sharing'
  allows: (caps: Capabilities) => boolean
}

/** The plan step's table: the gated features people ask about, cheapest first. */
export const PLAN_FEATURES: readonly PlanFeature[] = [
  { key: 'advancedTwap', allows: (c) => isAlgoAllowed(c, AlgoType.ALGO_TYPE_ADVANCED_TWAP) },
  { key: 'riskSlides', allows: (c) => hasFeature(c, BillingFeature.RiskSimulation) },
  { key: 'rfq', allows: (c) => isAlgoAllowed(c, AlgoType.ALGO_TYPE_RFQ) },
  { key: 'spreader', allows: (c) => isAlgoAllowed(c, AlgoType.ALGO_TYPE_SPREADER) },
  { key: 'arb', allows: (c) => isAlgoAllowed(c, AlgoType.ALGO_TYPE_CROSS_EXCHANGE_ARB) },
  { key: 'sharing', allows: (c) => getMaxSharedMembers(c) > 0 },
]

/**
 * The tutorials the finish card suggests next, per plan: the basics of placing an order for
 * Essential, the risk and hedging pages for Pro, the cross-venue and block tools for Custom.
 * A locked one still shows, marked with the plan it needs.
 */
export const UP_NEXT: Record<TourPlan, readonly TutorialId[]> = {
  essential: ['chat-order-form', 'positions', 'twap'],
  pro: ['risk-slides-page', 'hedge', 'advanced-twap'],
  custom: ['arb', 'rfq', 'risk-slides-page'],
}

export function upNextFor(plan: TourPlan | null): readonly TutorialId[] {
  return UP_NEXT[plan ?? 'essential']
}

/** What the tour reads off a subscription: real, or one the QA dock plays as. */
export type TourSubscription = Pick<
  GetSubscriptionResponse,
  'planLevel' | 'planLabel' | 'capabilities'
>

/**
 * A subscription for `plan` built from the public catalog, so the QA dock can play each
 * scenario with that plan's real capabilities: Essential is the free plan, Pro the Pro plan,
 * Custom the catalog's top plan under the CUSTOM label. Undefined when the catalog lacks it.
 */
export function simulatedSubscription(plans: Plan[], plan: TourPlan): TourSubscription | undefined {
  const known = plans
    .filter((p) => p.capabilities && planLevelOrder(p.level) !== Number.MAX_SAFE_INTEGER)
    .sort((a, b) => planLevelOrder(a.level) - planLevelOrder(b.level))
  const pick =
    plan === 'essential'
      ? known.find((p) => p.level === PlanLevel.PLAN_LEVEL_FREE)
      : plan === 'pro'
        ? known.find((p) => p.level === PlanLevel.PLAN_LEVEL_PRO)
        : known.at(-1)
  if (!pick) return undefined
  const planLabel =
    plan === 'essential' ? 'ESSENTIAL' : plan === 'custom' ? COMPED_PLAN_LABEL : pick.label
  return { planLevel: pick.level, planLabel, capabilities: pick.capabilities }
}
